import type { User } from 'hooks/services/users/types'
import { useMemo } from 'react'
import { useQuery, UseQueryOptions } from 'react-query'
import { useParams } from 'react-router-dom'
import { AvailabilityConstraintHistoriesGetRequest, AvailabilityConstraintHistory, PaginatedResponse } from 'types/availabilityConstraint'
import { logFactory } from '../../util'
import { RouteParameters } from '../../constants'
import { useAppointmentsService, useUsersService } from '../services'

type Props = Omit<AvailabilityConstraintHistoriesGetRequest, 'resource_id'>

const hookName = 'useAvailabilityConstraintHistories'
const log = logFactory(hookName)

type AvailabilityConstraintHistoriesPageResponse = PaginatedResponse<AvailabilityConstraintHistory> & {
  users: Map<string, User>
}

const useAvailabilityConstraintHistories = (params: Props, options?: UseQueryOptions<AvailabilityConstraintHistoriesPageResponse>) => {
  const { [RouteParameters.RESOURCE_ID]: resourceId } = useParams()
  const { getAvailabilityConstraintHistories, isReady } = useAppointmentsService()
  const { getUsers } = useUsersService()

  const request: AvailabilityConstraintHistoriesGetRequest = useMemo(
    () => ({
      ...params,
      resource_id: resourceId as string,
    }),
    [resourceId, params]
  )

  const fetchAvailabilityConstraintHistories = async (): Promise<AvailabilityConstraintHistoriesPageResponse> => {
    log(`🕰️ Fetching availability constraint histories...`, request)

    const historiesPage = await getAvailabilityConstraintHistories(request)

    const userIds = Array.from(
      new Set<string>(historiesPage.data.map((item: AvailabilityConstraintHistory) => item.created_by.id ?? '').filter(Boolean))
    )

    const usersPage = userIds.length > 0 ? await getUsers({ id: userIds }) : undefined
    const usersMap = new Map<string, User>()

    // These are helpful when displaying user info in the UI
    usersPage?.data.forEach((user) => {
      usersMap.set(user.gid, user)
      usersMap.set(user.id, user)
      usersMap.set(user.email, user)
    })

    log(`🕰️ Found availability constraint histories`, { histories: historiesPage, users: usersPage })

    return { ...historiesPage, users: usersMap }
  }

  const isQueryEnabled = Boolean(resourceId && isReady && (options?.enabled ?? true))

  return useQuery([hookName, request], fetchAvailabilityConstraintHistories, {
    enabled: isQueryEnabled,
  })
}

export { useAvailabilityConstraintHistories }
export type { AvailabilityConstraintHistoriesPageResponse }
